import type { FieldErrors, UseFormRegister } from 'react-hook-form'
import { Botao } from '../../../components/Botao'
import { Explicacao } from '../../../components/Explicacao'
import { CampoDeSelecao, CampoDeTexto, CampoDeTextoComSugestoes } from '../../../components/campos'
import { TERMOS_DA_OS } from '../termosDaOs'
import { FORMULARIO } from './textosDoFormulario'
import type { FormularioDeOs } from './esquema'

interface Props {
  indice: number
  register: UseFormRegister<FormularioDeOs>
  errors: FieldErrors<FormularioDeOs>
  /** Remover só aparece quando há mais de uma linha: a ordem exige ao menos uma. */
  aoRemover: (() => void) | null
}

/**
 * As unidades que a fiscalização já imprimiu em documento.
 *
 * Sugestão, não regra: o TRP praticado imprime 'Mês' numa tabela e 'UST' em outra para o MESMO
 * item, e o sistema tem de conseguir escrever o que estava no papel. A lista existe para que
 * 'mes', 'Mês' e 'MÊS' não virem três unidades no relatório.
 */
const UNIDADES_PRATICADAS = ['Mês', 'PF', 'UST'] as const

/**
 * Uma linha da seção 4.
 *
 * **A quantidade tem dois campos, e não um.** O documento escreve "25/30", e é isso que o sistema
 * guarda: `0,83` perderia R$ 731,48 na linha de sustentação de maio/2026 e `0,8333…` mentiria sobre
 * o papel. Quantidade inteira é a fração com 1 embaixo — a mesma forma para os dois casos evita o
 * campo que muda de significado conforme o mês.
 *
 * **O valor total da linha é digitado, não calculado.** Nenhuma precisão reproduz exatamente o
 * valor do contrato: 5.000.000 ÷ 10.500 dá 476,190476…, e nem duas nem quatro casas devolvem os
 * 5.000.000 de volta. O total é o que está no papel; a conferência do sistema aparece depois, na
 * tela da ordem, ao lado dele — e nenhum dos dois corrige o outro.
 *
 * @param props a posição da linha, o registrador, os erros e como removê-la
 * @returns os campos de uma linha
 */
export function LinhaDeItem({ indice, register, errors, aoRemover }: Props) {
  const erros = errors.itens?.[indice]

  return (
    <li className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900">{FORMULARIO.item.titulo(indice + 1)}</h3>
        {aoRemover && (
          <Botao tamanho="pequeno" aparencia="perigo" onClick={aoRemover}>
            {FORMULARIO.item.remover}
          </Botao>
        )}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-6">
        <CampoDeTexto
          rotulo={FORMULARIO.item.numero}
          className="sm:col-span-1"
          erro={erros?.item?.message}
          {...register(`itens.${indice}.item`)}
        />
        <div className="sm:col-span-5">
          <CampoDeTexto
            rotulo={FORMULARIO.item.descricao}
            erro={erros?.descricao?.message}
            {...register(`itens.${indice}.descricao`)}
          />
        </div>

        <div className="sm:col-span-2">
          <CampoDeSelecao
            rotulo={FORMULARIO.item.metrica}
            erro={erros?.metrica?.message}
            {...register(`itens.${indice}.metrica`)}
          >
            <option value="VALOR_FIXO">Valor fixo</option>
            <option value="PONTO_DE_FUNCAO">Ponto de Função</option>
          </CampoDeSelecao>
        </div>
        <div className="sm:col-span-2">
          <CampoDeTextoComSugestoes
            rotulo={FORMULARIO.item.unidade}
            ajuda={FORMULARIO.item.unidadeAjuda}
            sugestoes={UNIDADES_PRATICADAS}
            {...register(`itens.${indice}.unidade`)}
          />
        </div>
        <div className="sm:col-span-2">
          <CampoDeTexto rotulo={FORMULARIO.item.catser} {...register(`itens.${indice}.catser`)} />
        </div>

        <div className="sm:col-span-2">
          <CampoDeTexto
            rotulo={FORMULARIO.item.valorUnitario}
            inputMode="decimal"
            erro={erros?.valorUnitario?.message}
            {...register(`itens.${indice}.valorUnitario`)}
          />
        </div>
        <div className="sm:col-span-1">
          <CampoDeTexto
            rotulo={FORMULARIO.item.numerador}
            type="number"
            min={1}
            step={1}
            erro={erros?.quantidadeNumerador?.message}
            {...register(`itens.${indice}.quantidadeNumerador`)}
          />
        </div>
        <div className="sm:col-span-1">
          <CampoDeTexto
            rotulo={FORMULARIO.item.denominador}
            ajuda={FORMULARIO.item.fracaoAjuda}
            type="number"
            min={1}
            step={1}
            erro={erros?.quantidadeDenominador?.message}
            {...register(`itens.${indice}.quantidadeDenominador`)}
          />
        </div>
        <div className="sm:col-span-2">
          <CampoDeTexto
            rotulo={FORMULARIO.item.valorTotal}
            ajuda={FORMULARIO.item.valorTotalAjuda}
            inputMode="decimal"
            erro={erros?.valorTotalDeclarado?.message}
            {...register(`itens.${indice}.valorTotalDeclarado`)}
          />
        </div>
      </div>

      <p className="mt-2 flex items-center gap-1 text-xs text-slate-500">
        {TERMOS_DA_OS.quantidadeComoFracao.rotulo}
        <Explicacao
          titulo={TERMOS_DA_OS.quantidadeComoFracao.rotulo}
          texto={TERMOS_DA_OS.quantidadeComoFracao.ajuda}
          className="h-3.5 w-3.5"
        />
      </p>
    </li>
  )
}
